import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { hideLoading, showLoading } from 'react-redux-loading-bar';
import { pageViewEventHandler } from '@/analytics/events';
import { ROUTES } from '@/constants/routes';
import authActions from '@/redux/auth/actions';
import globalActions from '@/redux/global/actions';
import meActions from '@/redux/me/actions';
import { removeSelectedPopularItem } from '@/services/recommendations.service';
import { type RootState } from '@/types/app';

/**
 * True when a route change actually leaves the current page: not a shallow update (the store
 * page's product-modal query params) and not the same path with a different query.
 */
export const leavesCurrentPage = (url: string, currentPathname: string, shallow = false) =>
  !shallow && url.split(/[?#]/)[0] !== currentPathname;

/**
 * The query string after writing `parameters` over `search`. Array values (the selected menu tag
 * ids) serialize comma-separated. `removeFilter` drops `filter` once a menu tag selection is
 * cleared, so the URL stops re-applying it.
 */
export const buildRouteSearch = (
  search: string,
  parameters: Record<string, unknown>,
  removeFilter = false,
) => {
  const params = new URLSearchParams(search);

  Object.entries(parameters).forEach(([param, value]) => {
    params.set(param, Array.isArray(value) ? value.join(',') : String(value));
  });

  if (removeFilter) {
    params.delete('filter');
  }

  return params.toString();
};

/**
 * Global event handlers that dispatch Redux actions based on route changes.
 *
 * @returns null
 */
export const RouteHandler = () => {
  const dispatch = useDispatch();
  const router = useRouter();
  const activeTagIds = useSelector((state: RootState) => state.app.tags.activeTagIds);
  const basketOpen = useSelector((state: RootState) => state.app.global.basketOpen);
  const { persistentParameters, currentPath } = useSelector(
    (state: RootState) => state.app.global.routerStatus,
  );
  const previousTagIds = useRef(activeTagIds);

  // Triggered when a route starts to change.
  const handleRouteChangeStart = (url: string, { shallow }: { shallow?: boolean } = {}) => {
    dispatch(showLoading('page-transition'));
    // A connection toast's "Try again" re-dispatches the fetch that failed on THIS page; it
    // must not survive onto another one (see `globalActions.dismissRetryToast`).
    if (leavesCurrentPage(url, window.location.pathname, shallow)) {
      dispatch(globalActions.dismissRetryToast());
    }
    // Close account dropdown modal on route change
    dispatch(meActions.toggleAccountDropdown(false));
    // Close fulfillment modal on route change
    dispatch(globalActions.toggleFulfillmentModal(false));
    // Close delivery modal on route change
    dispatch(meActions.toggleReorderReceipt(false));
  };

  // Triggered when a route change is completed.
  const handleRouteChangeComplete = () => {
    if (currentPath !== window.location.pathname) {
      dispatch(globalActions.setRouterPathname(window.location.pathname));
    }
    let showBasket = false;

    if (router.route === ROUTES.STORE) {
      if (basketOpen || router.query.openCart === String(true)) {
        showBasket = true;
      }
    }

    /**
     * We reset the selected popular product if the user navigates
     * outside of the page -> store locator -> single store flow
     *
     * The store locator can exist at `/` or `/stores`, so we check both
     */
    if (![ROUTES.STORE, ROUTES.STORES, ROUTES.HOMEPAGE].includes(router.route)) {
      removeSelectedPopularItem();
    }

    // Ensure cart is always closed on route change unless it has the cartOpen query param
    dispatch(globalActions.toggleBasket(showBasket));

    if (typeof window !== 'undefined' && window.location) {
      // Consolidated pageview events
      pageViewEventHandler();
    }

    appendRouteParameters();

    window.setTimeout(() => {
      dispatch(hideLoading('page-transition'));
    }, 300);
  };

  useEffect(() => {
    // Listen to routeChangeStart and routeChangeComplete router events.
    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', handleRouteChangeComplete);

    dispatch(globalActions.hydratePersistentParameters(router.query));

    if (typeof window === 'object') {
      window.onload = () => {
        dispatch(globalActions.setRouterPathname(window.location.pathname));
        dispatch(authActions.reloadUser());
      };
    }

    // Clean up the router listeners when the component unmounts.
    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  }, [router]);

  useEffect(() => {
    // Only a selection going from some tags to none removes `filter`; an empty selection on page
    // load must leave a shared `?filter=` in place until the menu has read it.
    const filterCleared = Boolean(previousTagIds.current?.length) && !activeTagIds?.length;
    previousTagIds.current = activeTagIds;

    appendRouteParameters(filterCleared);
  }, [persistentParameters, activeTagIds]);

  const appendRouteParameters = (filterCleared = false) => {
    if ('URLSearchParams' in window) {
      const newParameters = {
        ...persistentParameters, // add required persistent route parameters
        ...(Boolean(activeTagIds?.length) &&
          router.pathname === ROUTES.STORE && { filter: activeTagIds }), // add filter tag IDs
      };
      const removeFilter = filterCleared && router.pathname === ROUTES.STORE;

      // ONLY rewrite the URL if we have new persistent parameters or filter tags to work with
      if (Object.keys(newParameters).length > 0 || removeFilter) {
        const url = new URL(window.location.href);
        const search = buildRouteSearch(url.search, newParameters, removeFilter);

        const as = search ? `${url.pathname}?${search}` : url.pathname;
        const newWindowHistoryState = {
          ...window.history.state,
          as,
        };

        // replace this point in browser history with updated 'as' value so you may click the back button as one would expect
        window.history.replaceState(newWindowHistoryState, '', as);
      }
    }
  };

  // This component doesn't need to return anything.
  return null;
};
